/**
 * Subject reads — the client half of the governed sensitive-read doors (#2444
 * slice 4, contract C6).
 *
 * An entity whose registry row says `read_audit = 'subject'` has had its direct
 * API SELECT revoked (core 0.386.0, `core.apply_read_audit_grants`). Its rows
 * are read ONLY through three generic core doors, each of which appends one
 * chained `record.viewed` per row it actually returns:
 *
 *   core.subject_get(entity, id)                         -> jsonb | null
 *   core.subject_get_many(entity, ids uuid[] ≤ 200)      -> setof jsonb
 *   core.subject_list(entity, filter, order_by, descending, limit 1..200, offset)
 *                                                        -> setof jsonb
 *
 * They are VOLATILE, so they are always called by POST (`rpcTable` /
 * `rpcScalar`): PostgREST runs a GET and a STABLE call READ ONLY, where the
 * receipt's INSERT would fail with 25006.
 *
 * ── the rule this file enforces: translate faithfully or REFUSE ────────────
 * The door's filter vocabulary is CLOSED — `eq`, `in`, `is_null`, `not_null`
 * on registered fields, ordered by ONE field then `id`, at most 200 rows. A
 * host's read is written in PostgREST's much larger language, so every read of
 * a subject entity passes through `toSubjectQuery`, which either expresses the
 * SAME question in the door's vocabulary or throws
 * `SubjectReadUnsupportedError` naming exactly what could not be expressed.
 *
 * It never approximates. Dropping an `ilike` returns MORE rows than asked —
 * and on a subject entity every one of those extra rows is a `record.viewed`
 * for a record nobody asked to see. Clamping a 500-row request to 200 returns
 * a list that looks complete and is not. Rewriting `desc.nullslast` to the
 * door's `desc` (nulls first) re-orders the screen. Each of those is a
 * different wrong answer, so each is a refusal a host must handle — by asking
 * a question the door can answer, or by telling the person it cannot.
 *
 * The door re-checks everything server-side (22023). This client-side check
 * exists so the refusal is a TYPED error with a sentence, before a request is
 * spent, rather than a 400 with a Postgres message.
 */
import type { Row, SelectOptions } from './transport.js'
import type { FilterLeaf, PgFilter } from './filters.js'

/** How reads of an entity are evidenced (`core.entity_type.read_audit`). */
export type ReadAudit = 'request' | 'subject' | 'none'

/** The door's hard page bound. A larger request is REFUSED by the door, not clamped. */
export const SUBJECT_MAX_ROWS = 200
/** The door's clause bound. */
export const SUBJECT_MAX_CLAUSES = 32

/** One clause of the door's closed filter vocabulary. */
export type SubjectScalar = string | number | boolean
export type SubjectClause =
  | { field: string; op: 'eq'; value: SubjectScalar }
  | { field: string; op: 'in'; value: SubjectScalar[] }
  | { field: string; op: 'is_null' }
  | { field: string; op: 'not_null' }

/** A read, already expressed in the door's vocabulary. */
export interface SubjectQuery {
  filter: SubjectClause[]
  orderBy: string | null
  descending: boolean
  limit: number
  offset: number
  /** A plain projection the CLIENT applies to the door's rows; null = every column the door returns. */
  columns: string[] | null
}

/**
 * The refusal. Modelled on `WriteDoorClosedError`: it says what the read ASKED
 * that the governed door cannot express — never "permission denied", because
 * nothing about the caller's authority is being decided here.
 */
export class SubjectReadUnsupportedError extends Error {
  /** Stable machine marker — survives bundling/duplication where `instanceof` does not. */
  readonly code = 'subject_read_unsupported'
  constructor(
    readonly entity: string,
    /** What was asked: `filter`, `order`, `limit`, `offset`, `columns`, `where`. */
    readonly feature: 'filter' | 'order' | 'limit' | 'offset' | 'columns' | 'where',
    readonly detail: string,
  ) {
    super(
      `'${entity}' is read through its governed door, which records every record it returns. `
      + `That door cannot express this read (${feature}: ${detail}), so nothing was sent. `
      + `It filters by is / is any of / is set / is not set on registered fields, orders by one field, `
      + `and returns at most ${SUBJECT_MAX_ROWS} rows per page.`,
    )
    this.name = 'SubjectReadUnsupportedError'
  }
}

/** Structural check for the refusal above — works across package copies. */
export function isSubjectReadUnsupported(e: unknown): e is SubjectReadUnsupportedError {
  return typeof e === 'object' && e !== null && (e as { code?: unknown }).code === 'subject_read_unsupported'
}

/** An identifier PostgREST would accept as a bare column (no alias, cast, embed or JSON path). */
const IDENT = /^[A-Za-z_][A-Za-z0-9_]*$/

/**
 * Undo the quoting `filters.ts`'s `lit()` applies (`"a.b"`, `\"` inside), the
 * same convention PostgREST reads. A value that is not wrapped is taken as-is.
 */
function unquote(s: string): string {
  if (s.length >= 2 && s.startsWith('"') && s.endsWith('"')) {
    return s.slice(1, -1).replace(/\\(["\\])/g, '$1')
  }
  return s
}

/** Split a PostgREST `in.(…)` list, honouring double-quoted items. Null ⇒ malformed. */
function splitList(body: string): string[] | null {
  const out: string[] = []
  let cur = ''
  let quoted = false
  let wasQuoted = false
  for (let i = 0; i < body.length; i++) {
    const c = body[i]!
    if (quoted) {
      if (c === '\\' && i + 1 < body.length) { cur += body[++i]; continue }
      if (c === '"') { quoted = false; continue }
      cur += c
      continue
    }
    if (c === '"') { quoted = true; wasQuoted = true; continue }
    if (c === ',') { out.push(cur); cur = ''; wasQuoted = false; continue }
    cur += c
  }
  if (quoted) return null
  if (cur !== '' || wasQuoted || out.length > 0) out.push(cur)
  return out
}

/** One PostgREST-native filter → one door clause, or a refusal. */
function clauseOf(entity: string, f: PgFilter): SubjectClause {
  const field = f.key
  if (!IDENT.test(field) || field === 'or' || field === 'and' || field === 'not') {
    throw new SubjectReadUnsupportedError(entity, 'filter', `'${field}' is a logic group or a path, not one field`)
  }
  const v = f.value
  if (v === 'is.null') return { field, op: 'is_null' }
  if (v === 'not.is.null') return { field, op: 'not_null' }
  // `is.true` is `= true` (a NULL is neither): exactly the door's eq on a boolean.
  if (v === 'is.true') return { field, op: 'eq', value: true }
  if (v === 'is.false') return { field, op: 'eq', value: false }
  if (v.startsWith('eq.')) return { field, op: 'eq', value: unquote(v.slice(3)) }
  if (v.startsWith('in.(') && v.endsWith(')')) {
    const items = splitList(v.slice(4, -1))
    if (!items || items.length === 0 || items.length > SUBJECT_MAX_ROWS) {
      throw new SubjectReadUnsupportedError(entity, 'filter',
        `${field} is any of: needs 1..${SUBJECT_MAX_ROWS} values`)
    }
    return { field, op: 'in', value: items }
  }
  const operator = v.split('.').slice(0, v.startsWith('not.') ? 2 : 1).join('.')
  throw new SubjectReadUnsupportedError(entity, 'filter', `${field} ${operator || v} is outside is / is any of / is set / is not set`)
}

/** `where` equality → door clauses. A null or non-scalar value is refused, never guessed. */
function whereClauses(entity: string, where: Row): SubjectClause[] {
  const out: SubjectClause[] = []
  for (const [field, value] of Object.entries(where)) {
    if (!IDENT.test(field)) throw new SubjectReadUnsupportedError(entity, 'where', `'${field}' is not one field`)
    if (value === null || value === undefined) {
      // The PostgREST transport would send `eq.null` — a comparison with the
      // STRING 'null'. Neither that nor `is_null` can be claimed as what the
      // caller meant, so the caller must say `is.null` explicitly.
      throw new SubjectReadUnsupportedError(entity, 'where', `${field} = null is ambiguous; filter with is.null`)
    }
    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
      throw new SubjectReadUnsupportedError(entity, 'where', `${field} needs one scalar value`)
    }
    out.push({ field, op: 'eq', value })
  }
  return out
}

/**
 * A PostgREST order clause → one field and a direction, faithfully.
 *
 * The door orders `field dir, id dir` with Postgres's own NULL placement: NULLS
 * LAST ascending, NULLS FIRST descending. A request whose null modifier AGREES
 * with that is translated; one that disagrees (`desc.nullslast`,
 * `asc.nullsfirst`) is refused, because honouring the direction while ignoring
 * the modifier moves every blank to the other end of the list.
 */
function orderOf(entity: string, order: string | undefined): { orderBy: string | null; descending: boolean } {
  if (order == null || order.trim() === '') return { orderBy: null, descending: false }
  const terms = order.split(',').map((t) => t.trim()).filter(Boolean)
  if (terms.length > 1) {
    throw new SubjectReadUnsupportedError(entity, 'order', `orders by ${terms.length} fields; the door orders by one`)
  }
  const parts = terms[0]!.split('.')
  const field = parts[0]!
  if (!IDENT.test(field)) throw new SubjectReadUnsupportedError(entity, 'order', `'${field}' is not one field`)
  let descending = false
  let nulls: 'first' | 'last' | null = null
  for (const p of parts.slice(1)) {
    if (p === 'asc') descending = false
    else if (p === 'desc') descending = true
    else if (p === 'nullsfirst') nulls = 'first'
    else if (p === 'nullslast') nulls = 'last'
    else throw new SubjectReadUnsupportedError(entity, 'order', `'${terms[0]}' is not a plain ordering`)
  }
  const doorNulls = descending ? 'first' : 'last'
  if (nulls !== null && nulls !== doorNulls) {
    throw new SubjectReadUnsupportedError(entity, 'order',
      `${field} ${descending ? 'descending' : 'ascending'} with blanks ${nulls}; the door puts blanks ${doorNulls}`)
  }
  return { orderBy: field, descending }
}

function columnsOf(entity: string, columns: string | undefined): string[] | null {
  if (columns == null || columns.trim() === '' || columns.trim() === '*') return null
  const list = columns.split(',').map((c) => c.trim())
  for (const c of list) {
    if (!IDENT.test(c)) {
      throw new SubjectReadUnsupportedError(entity, 'columns', `'${c}' is an alias, cast, embed or path, not one column`)
    }
  }
  return list
}

/**
 * Express a PostgREST-shaped read in the door's closed vocabulary — or refuse.
 *
 * `limit` is REQUIRED and at most `SUBJECT_MAX_ROWS`: an unbounded PostgREST
 * read returns every row, and the door cannot, so the host must page and say
 * that it pages. There is no default that would be honest.
 */
export function toSubjectQuery(entity: string, opts: SelectOptions | undefined): SubjectQuery {
  const limit = opts?.limit
  if (limit == null) {
    throw new SubjectReadUnsupportedError(entity, 'limit',
      `no limit; a governed read returns at most ${SUBJECT_MAX_ROWS} rows per page, so the caller must page`)
  }
  if (!Number.isInteger(limit) || limit < 1 || limit > SUBJECT_MAX_ROWS) {
    throw new SubjectReadUnsupportedError(entity, 'limit', `${limit} rows; the page is 1..${SUBJECT_MAX_ROWS}`)
  }
  const offset = opts?.offset ?? 0
  if (!Number.isInteger(offset) || offset < 0) {
    throw new SubjectReadUnsupportedError(entity, 'offset', `${offset}`)
  }
  const filter: SubjectClause[] = [
    ...whereClauses(entity, opts?.where ?? {}),
    ...(opts?.filters ?? []).map((f) => clauseOf(entity, f)),
  ]
  if (filter.length > SUBJECT_MAX_CLAUSES) {
    throw new SubjectReadUnsupportedError(entity, 'filter', `${filter.length} clauses; at most ${SUBJECT_MAX_CLAUSES}`)
  }
  return { filter, ...orderOf(entity, opts?.order), limit, offset, columns: columnsOf(entity, opts?.columns) }
}

/** The RPC arguments `core.subject_list` takes, for a query this module produced. */
export function subjectListArgs(entity: string, q: SubjectQuery): Row {
  return {
    p_entity: entity,
    p_filter: q.filter,
    p_order_by: q.orderBy,
    p_descending: q.descending,
    p_limit: q.limit,
    p_offset: q.offset,
  }
}

/** Apply a plain projection to the door's rows (the door returns every readable column). */
export function project<T>(rows: Row[], columns: string[] | null): T[] {
  if (!columns) return rows as T[]
  return rows.map((r) => Object.fromEntries(columns.map((c) => [c, r[c]])) as T)
}

// ── the HOST's half: saved-view leaves → a read the door can answer ─────────
//
// `filters.ts` translates the console's view grammar (`@solum/rules`' `Leaf`)
// to PostgREST for an ordinary entity. For a subject entity the target is
// narrower, and so is what counts as faithful:
//
//   · `eq` in the JS evaluator is CASE-INSENSITIVE (`String(v).toLowerCase()`).
//     The door's `eq` is exact. They agree only where a value has no case to
//     disagree about: numbers, money, percents, dates, booleans, references —
//     and `option`, whose values are a registered vocabulary of keys, so a
//     stored value and a chosen filter value are the same key or not a match
//     at all. On free TEXT they disagree, so text `eq` is refused. (The
//     ordinary path gets case-insensitivity from `ilike`, which the door does
//     not have.)
//   · `in` is `Array.includes` — exact on every kind, exactly the door's `in`.
//   · `empty` is `v == null || v === ''`. Where `''` cannot be stored (a
//     number, a date, a boolean, a reference) that IS `is_null`; on text it is
//     not, and is refused.
//   · `not { empty }` is `not_null` on the same kinds.
//   · `all` of translatable leaves is their conjunction. `any`, other `not`s,
//     ranges and substrings are outside the vocabulary.

/** Kinds whose `eq` is exact under both evaluators — see above. */
const EXACT_EQ: ReadonlySet<string> = new Set(['number', 'money', 'percent', 'date', 'boolean', 'ref', 'uuid', 'option'])
/** Kinds that cannot hold `''`, so `empty` is exactly `is_null`. */
const NO_EMPTY_STRING: ReadonlySet<string> = new Set(['number', 'money', 'percent', 'date', 'datetime', 'boolean', 'ref', 'uuid'])

/**
 * May a leaf with this operator, on a field of this kind, be answered by the
 * door with the SAME rows the view's own evaluator would pick? The one
 * predicate both the translator and a host's operator menu read, so a menu can
 * never offer what the translation would then refuse.
 */
export function subjectLeafSupported(op: string, kind: string | undefined): boolean {
  switch (op) {
    case 'in': return true
    case 'eq': return kind !== undefined && EXACT_EQ.has(kind)
    case 'empty': return kind !== undefined && NO_EMPTY_STRING.has(kind)
    default: return false
  }
}

/** Why a leaf was refused, in words for a person. */
function leafReason(op: string | undefined, kind: string | undefined): string {
  if (!op) return 'a condition group this record type\'s governed read cannot apply'
  if (op === 'eq') return kind === undefined
    ? '"is" on a column the field dictionary does not describe'
    : '"is" on free text (it ignores case, and the governed read compares exactly)'
  if (op === 'empty') return '"is empty" on text (empty text is not the same as no value)'
  return `the "${op}" condition`
}

export type SubjectFilterTranslation =
  | { ok: true; filters: PgFilter[] }
  | { ok: false; field: string | null; op: string | null; reason: string }

/**
 * Saved-view leaves → PostgREST filters that `toSubjectQuery` accepts, or a
 * refusal carrying the FIRST leaf it could not express and why. All or
 * nothing, like `filtersToPostgrest`: a partially applied view is neither the
 * view nor the list.
 *
 * `binds` resolves `$me` and friends; an unresolvable bind is refused.
 */
export function leavesToSubjectFilters(
  leaves: FilterLeaf[],
  opts: { kindOf?: (field: string) => string | undefined; binds?: Record<string, unknown> } = {},
): SubjectFilterTranslation {
  const out: PgFilter[] = []
  const lit = (v: unknown) => {
    const s = String(v ?? '')
    return /[,.:()"\s]/.test(s) ? `"${s.replace(/(["\\])/g, '\\$1')}"` : s
  }
  const refuse = (leaf: FilterLeaf, reason: string): SubjectFilterTranslation =>
    ({ ok: false, field: leaf.field ?? null, op: leaf.op ?? null, reason })
  const walk = (leaf: FilterLeaf): SubjectFilterTranslation | null => {
    if (leaf.all) {
      for (const l of leaf.all) { const r = walk(l); if (r) return r }
      return null
    }
    if (leaf.not && leaf.not.op === 'empty' && leaf.not.field && !leaf.not.all && !leaf.not.any && !leaf.not.not) {
      const kind = opts.kindOf?.(leaf.not.field)
      if (!subjectLeafSupported('empty', kind)) return refuse(leaf.not, '"is not empty" on text (empty text is not the same as no value)')
      out.push({ key: leaf.not.field, value: 'not.is.null' })
      return null
    }
    if (leaf.any || leaf.not || !leaf.field || !leaf.op) return refuse(leaf, leafReason(undefined, undefined))
    const kind = opts.kindOf?.(leaf.field)
    if (!subjectLeafSupported(leaf.op, kind)) return refuse(leaf, leafReason(leaf.op, kind))
    let value = leaf.value
    const bind = (v: unknown): { ok: boolean; v: unknown } => {
      if (typeof v !== 'string' || !v.startsWith('$')) return { ok: true, v }
      if (opts.binds && v in opts.binds && opts.binds[v] != null) return { ok: true, v: opts.binds[v] }
      return { ok: false, v }
    }
    if (leaf.op === 'empty') { out.push({ key: leaf.field, value: 'is.null' }); return null }
    if (leaf.op === 'in') {
      if (!Array.isArray(value) || value.length === 0 || value.length > SUBJECT_MAX_ROWS) {
        return refuse(leaf, `"is any of" needs 1..${SUBJECT_MAX_ROWS} values`)
      }
      const vals: unknown[] = []
      for (const item of value) {
        const b = bind(item)
        if (!b.ok) return refuse(leaf, `a value that depends on "${String(item)}", which is not known here`)
        vals.push(b.v)
      }
      out.push({ key: leaf.field, value: `in.(${vals.map(lit).join(',')})` })
      return null
    }
    const b = bind(value)
    if (!b.ok) return refuse(leaf, `a value that depends on "${String(value)}", which is not known here`)
    value = b.v
    if (value === null || value === undefined || value === '') return refuse(leaf, '"is" with no value')
    out.push({ key: leaf.field, value: `eq.${lit(value)}` })
    return null
  }
  for (const leaf of leaves) {
    const r = walk(leaf)
    if (r) return r
  }
  return { ok: true, filters: out }
}

/**
 * A saved-view sort → the ONE ordering the door can apply, plus the terms it
 * could not (reported, so a host can SAY the list is ordered by fewer keys than
 * the view names — never drop them silently). No null modifier is asked for:
 * the door's own placement (blanks last ascending, first descending) is what
 * the rows will actually be in, so it is what the request says.
 */
export function sortToSubject(sortSpec: unknown[] | undefined): { order: string | undefined; dropped: string[] } {
  const terms: { key: string; direction: 'asc' | 'desc' }[] = []
  const spec = sortSpec ?? []
  const seen = new Set<string>()
  for (let i = 0; i < spec.length; i += 2) {
    const key = spec[i]
    if (typeof key !== 'string' || key.length === 0 || seen.has(key)) continue
    seen.add(key)
    terms.push({ key, direction: spec[i + 1] === 'desc' ? 'desc' : 'asc' })
  }
  if (terms.length === 0) return { order: undefined, dropped: [] }
  const [first, ...rest] = terms
  return { order: `${first!.key}.${first!.direction}`, dropped: rest.map((t) => t.key) }
}
